import React, { useState, useEffect } from 'react';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  Refresh as ArrowPathIcon
} from '@mui/icons-material';
import {
  getSalesPlaybooks,
  createSalesPlaybook,
  deleteSalesPlaybook,
  toggleSalesPlaybookStatus,
  bulkUpdateSalesPlaybookRoles,
  type SalesPlaybook,
  type SalesPlaybookTag
} from '../services/apiService';
import { getPlaybookNameLabel } from '../constants/labels';
import AddSalesPlaybookModal from './AddSalesPlaybookModal';
import PlaybookLibraryManagement from './PlaybookLibraryManagement';
import PlaybookIngestionReview from './PlaybookIngestionReview';
import { Button } from './ui/Button';
import { Dropdown } from './ui/Dropdown';
import { useConfirm } from './ui/ConfirmDialog';

const SALES_PLAYBOOK_TAG_LABELS: Record<SalesPlaybookTag, string> = {
  situation: 'Situation',
  pain: 'Pain',
  cost_math: 'Cost Math',
  authority: 'Authority',
  timeline: 'Timeline',
  close_trigger: 'Close Trigger'
};

interface SalesPlaybookManagementProps {
  onRefresh?: () => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  /** Opens the draft-review workflow from the Imports & Drafts section. */
  openAiDraftSignal?: number;
  /** Opens the manual playbook editor from the Overview Add Content menu. */
  openAddSignal?: number;
  canGenerateFromPublishedKb?: boolean;
  onReviewKnowledgeBaseDraft?: () => void;
}

const SalesPlaybookManagement: React.FC<SalesPlaybookManagementProps> = ({
  setError,
  setSuccess,
  openAiDraftSignal,
  openAddSignal,
  canGenerateFromPublishedKb = true,
  onReviewKnowledgeBaseDraft,
}) => {
  const confirm = useConfirm();
  const [activeSubTab, setActiveSubTab] = useState<'playbooks' | 'library'>('playbooks');
  const [playbooks, setPlaybooks] = useState<SalesPlaybook[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlaybookKey, setSelectedPlaybookKey] = useState<string>('');
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());

  // Modal states
  const [isAddSalesPlaybookModalOpen, setIsAddSalesPlaybookModalOpen] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState<SalesPlaybook | null>(null);
  const [isAiDraftReviewOpen, setIsAiDraftReviewOpen] = useState(false);
  const [draftReviewEntry, setDraftReviewEntry] = useState<'browse' | 'generate'>('browse');

  const fetchPlaybooks = async () => {
    setLoading(true);
    try {
      const response = await getSalesPlaybooks(1, 100);
      const fetchedPlaybooks = response.salesPlaybooks || [];
      setPlaybooks(fetchedPlaybooks);

      // Auto-select first playbook if none selected and playbooks exist
      if (!selectedPlaybookKey && fetchedPlaybooks.length > 0) {
        setSelectedPlaybookKey(fetchedPlaybooks[0].playbookName);
        setExpandedRoles(new Set(fetchedPlaybooks[0].roles.map(r => r._id)));
      }
    } catch (error) {
      console.error('Error fetching sales playbook:', error);
      setError('Failed to load sales playbook');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaybooks();
    // fetchPlaybooks is a plain function rebuilt on every render; this is a deliberate
    // fetch-once-on-mount effect, so listing it would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Do not open during the initial mount; only an explicit parent signal
  // should take a manager into the draft-review workflow.
  useEffect(() => {
    if (openAiDraftSignal === undefined) return;
    if (!canGenerateFromPublishedKb) {
      setError('Publish knowledge base content before generating a playbook.');
      return;
    }
    setActiveSubTab('playbooks');
    setDraftReviewEntry('browse');
    setIsAiDraftReviewOpen(true);
  }, [openAiDraftSignal, canGenerateFromPublishedKb, setError]);

  useEffect(() => {
    if (openAddSignal === undefined) return;
    setActiveSubTab('playbooks');
    setEditingPlaybook(null);
    setIsAddSalesPlaybookModalOpen(true);
  }, [openAddSignal]);

  const openDraftCreator = () => {
    if (!canGenerateFromPublishedKb) {
      setError('Publish knowledge base content before generating a playbook.');
      return;
    }
    setDraftReviewEntry('generate');
    setIsAiDraftReviewOpen(true);
  };

  // Get selected playbook object
  const selectedPlaybook = playbooks.find(p => p.playbookName === selectedPlaybookKey) || null;

  const handlePlaybookChange = (playbookKey: string) => {
    setSelectedPlaybookKey(playbookKey);
    const chosenPlaybook = playbooks.find(p => p.playbookName === playbookKey);
    if (chosenPlaybook) {
      setExpandedRoles(new Set(chosenPlaybook.roles.map(r => r._id)));
    }
  };

  const toggleRoleExpanded = (roleId: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId);
    } else {
      newExpanded.add(roleId);
    }
    setExpandedRoles(newExpanded);
  };

  const handleSaveSalesPlaybook = async (data: {
    playbookDisplayName: string;
    roles: {
      originalId?: string;
      displayName: string;
      pitchBullets: Array<string | { _id: string; text: string }>;
      advancedBullets: Array<string | { _id: string; text: string }>;
      questions: { _id?: string; text: string; tags: SalesPlaybookTag[] }[];
    }[];
  }) => {
    try {
      const isEditing = !!editingPlaybook;

      // Check if playbook exists
      const existingPlaybook = playbooks.find(
        p => p.displayName.toLowerCase() === data.playbookDisplayName.toLowerCase()
      );

      // Create playbook if it doesn't exist
      let currentPlaybook: SalesPlaybook;
      if (!existingPlaybook) {
        currentPlaybook = await createSalesPlaybook(data.playbookDisplayName);
        setSuccess(`Playbook "${data.playbookDisplayName}" created`);
      } else {
        currentPlaybook = existingPlaybook;
      }

      // Use single bulk API call to update all roles at once
      await bulkUpdateSalesPlaybookRoles(currentPlaybook.playbookName, data.roles);

      setSuccess(isEditing ? 'Sales playbook updated successfully' : 'Sales playbook added successfully');
      setIsAddSalesPlaybookModalOpen(false);
      setEditingPlaybook(null);
      await fetchPlaybooks();

      // Select the newly added/updated playbook
      setSelectedPlaybookKey(currentPlaybook.playbookName);
    } catch (error: any) {
      setError(error.message || 'Failed to save sales playbook');
    }
  };

  const handleDeletePlaybook = async (playbook: SalesPlaybook) => {
    if (!(await confirm({
      title: `Delete "${playbook.displayName}"?`,
      message: 'This will delete all roles, pitch bullets, and questions. This cannot be undone.',
      confirmLabel: 'Delete playbook',
      variant: 'destructive',
    }))) {
      return;
    }

    try {
      await deleteSalesPlaybook(playbook.playbookName);
      setSuccess('Sales playbook deleted successfully.');
      if (selectedPlaybookKey === playbook.playbookName) {
        setSelectedPlaybookKey('');
      }
      await fetchPlaybooks();
    } catch (error: any) {
      setError(error.message || 'Failed to delete playbook');
    }
  };

  const handleToggleStatus = async (playbook: SalesPlaybook) => {
    try {
      await toggleSalesPlaybookStatus(playbook.playbookName);
      setSuccess(`${playbook.displayName} status updated`);
      await fetchPlaybooks();
    } catch (error: any) {
      setError(error.message || 'Failed to toggle status');
    }
  };

  const handleEditPlaybook = (playbook: SalesPlaybook) => {
    setEditingPlaybook(playbook);
    setIsAddSalesPlaybookModalOpen(true);
  };

  // Return all roles - no filtering
  const getDisplayRoles = (playbook: SalesPlaybook) => {
    return playbook.roles;
  };

  return (
    <div className="flex flex-col h-full relative">
      {/* Refresh overlay */}
      {loading && playbooks.length > 0 && activeSubTab === 'playbooks' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
          <div className="flex flex-col items-center gap-2">
            <ArrowPathIcon className="animate-spin text-signature" style={{ fontSize: '2.5rem' }} />
            <span className="text-sm font-medium text-ink-secondary">Refreshing...</span>
          </div>
        </div>
      )}

      {/* Sub-tab navigation */}
      <div className="kb-playbook-subtabs flex bg-surface px-4">
        <button
          onClick={() => setActiveSubTab('playbooks')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${activeSubTab === 'playbooks' ? 'is-active' : ''}`}
        >
          Playbooks
        </button>
        <button
          onClick={() => setActiveSubTab('library')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${activeSubTab === 'library' ? 'is-active' : ''}`}
        >
          Library
        </button>
      </div>

      {activeSubTab === 'library' ? (
        <PlaybookLibraryManagement setError={setError} setSuccess={setSuccess} />
      ) : (
      <>
      {/* Description */}
      <div className="kb-playbook-guide px-4 py-3">
        <p className="text-sm text-ink-secondary">
          <span className="font-medium">How it works:</span> Create named playbooks (e.g., "Cleaning"), then add roles within each playbook (e.g., "Facility Manager", "Procurement"). Each role has its own intro pitch, qualifying questions, and deal progression talking points. During a call, salespeople select their playbook and role to get tailored guidance.
        </p>
      </div>

      {/* Header with Playbook Dropdown and Add Button */}
      <div className="kb-playbook-toolbar px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <Dropdown
            label={getPlaybookNameLabel()}
            width="wide"
            placeholder="Select a playbook..."
            value={selectedPlaybookKey}
            onChange={handlePlaybookChange}
            disabled={loading}
            options={playbooks.map((playbook) => ({
              value: playbook.playbookName,
              label: playbook.displayName || 'Not named',
            }))}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={fetchPlaybooks}
            variant="tertiary"
            size="sm"
            title="Refresh"
            disabled={loading}
            className="p-2"
          >
            <ArrowPathIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: '1.25rem' }} />
          </Button>
          <Button
            onClick={() => {
              setEditingPlaybook(null);
              setIsAddSalesPlaybookModalOpen(true);
            }}
            variant="primary"
            className="flex items-center gap-2"
          >
            <PlusIcon style={{ fontSize: '1.25rem' }} />
            Add Sales Playbook
          </Button>
          <button
            type="button"
            onClick={openDraftCreator}
            className="kb-button kb-button-secondary px-4 py-2 flex items-center gap-2"
          >
            Generate from published KB
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {loading && playbooks.length === 0 ? (
          <div className="p-8 text-center">
            <ArrowPathIcon className="animate-spin mx-auto text-ink-muted" style={{ fontSize: '2rem' }} />
            <p className="mt-2 text-ink-muted">Loading sales playbook...</p>
          </div>
        ) : !selectedPlaybook ? (
          <div className="p-8 text-center text-ink-muted">
            <p className="text-lg mb-2">
              {playbooks.length === 0 ? 'No sales playbook yet' : 'Select a playbook to view its details'}
            </p>
            <p className="text-sm">
              Click "Add Sales Playbook" to create one with a name, role, pitch, and questions.
            </p>
            {!canGenerateFromPublishedKb && (
              <p className="mx-auto mt-3 max-w-md text-xs text-ink-secondary">
                Publish knowledge base content before generating a playbook.
                {onReviewKnowledgeBaseDraft && (
                  <button type="button" onClick={onReviewKnowledgeBaseDraft} className="ml-2 font-medium underline text-signature hover:text-signature-strong">
                    Review starter draft
                  </button>
                )}
              </p>
            )}
          </div>
        ) : (
          <div className="kb-playbook-detail p-5">
            {/* Playbook Header — same shape as the Landmines/Competitors row
                header: a plain status icon next to the name, then a
                separate Activate/Deactivate toggle plus icon-only Edit/
                Delete in the action group, instead of one button doing
                double duty as both status readout and toggle. */}
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="heading-4">{selectedPlaybook.displayName || 'Not named'}</h2>
                  {selectedPlaybook.isActive ? (
                    <CheckCircleIcon className="text-success" style={{ fontSize: '1rem' }} titleAccess="Active — available on live calls" />
                  ) : (
                    <XCircleIcon className="text-ink-muted" style={{ fontSize: '1rem' }} titleAccess="Inactive — hidden from live calls" />
                  )}
                </div>
                <p className="text-sm text-ink-muted">
                  {getDisplayRoles(selectedPlaybook).length} role{getDisplayRoles(selectedPlaybook).length !== 1 ? 's' : ''} | {selectedPlaybook.roles.reduce((sum, r) => sum + r.questions.length, 0)} total questions
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleStatus(selectedPlaybook)}
                  className={`px-3 py-1 rounded text-sm ${
                    selectedPlaybook.isActive
                      ? 'bg-surface-sunken text-ink-secondary hover:bg-surface-subtle hover:text-ink'
                      : 'bg-signature text-on-signature hover:bg-signature-strong'
                  }`}
                >
                  {selectedPlaybook.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleEditPlaybook(selectedPlaybook)}
                  className="text-signature hover:text-signature-strong p-1"
                  title="Edit"
                >
                  <PencilIcon style={{ fontSize: '1.25rem' }} />
                </button>
                <button
                  onClick={() => handleDeletePlaybook(selectedPlaybook)}
                  className="p-1 text-error hover:opacity-80"
                  title="Delete playbook"
                >
                  <TrashIcon style={{ fontSize: '1.25rem' }} />
                </button>
              </div>
            </div>

            {/* Roles List - Read Only View */}
            <div className="space-y-2">
              {getDisplayRoles(selectedPlaybook).map(role => (
                <div key={role._id} className="kb-playbook-role border border-hairline rounded-lg bg-surface">
                  {/* Role Header */}
                  <div
                    className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-surface-subtle"
                    onClick={() => toggleRoleExpanded(role._id)}
                  >
                    <div className="flex items-center gap-2">
                      {expandedRoles.has(role._id) ? (
                        <ExpandLessIcon className="text-ink-muted" />
                      ) : (
                        <ExpandMoreIcon className="text-ink-muted" />
                      )}
                      <div>
                        <h3 className="text-sm font-semibold text-ink uppercase tracking-wide">{role.displayName}</h3>
                        <p className="text-sm text-ink-muted">
                          {role.pitch.length} pitch{role.pitch.length !== 1 ? 'es' : ''}, {role.questions.length} question{role.questions.length !== 1 ? 's' : ''}, {role.advanced?.length || 0} deal progression
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Role Content (Expanded) - Read Only - 3 Column Layout */}
                  {expandedRoles.has(role._id) && (
                    <div className="kb-playbook-role-content border-t border-hairline p-4">
                      <div className="kb-playbook-columns grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Intro Pitch Section */}
                        <div className="bg-surface-subtle rounded-lg p-3 md:border-r md:border-hairline md:rounded-r-none md:pr-4">
                          <h4 className="text-sm font-semibold text-ink-secondary uppercase tracking-wide mb-2">
                            Intro Pitch
                          </h4>
                          {role.pitch.length === 0 ? (
                            <p className="text-sm text-ink-muted italic">No pitch bullets</p>
                          ) : (
                            <ul className="space-y-1">
                              {role.pitch
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map(pitch => (
                                  <li key={pitch._id} className="flex items-start gap-2">
                                    <span className="text-ink-muted mt-0.5">-</span>
                                    <span className="text-sm text-ink-secondary">{pitch.text}</span>
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>

                        {/* Qualifying Questions Section */}
                        <div className="bg-surface-subtle rounded-lg p-3 md:border-r md:border-hairline md:rounded-none md:px-4">
                          <h4 className="text-sm font-semibold text-ink-secondary uppercase tracking-wide mb-2">
                            Qualifying Questions
                          </h4>
                          {role.questions.length === 0 ? (
                            <p className="text-sm text-ink-muted italic">No questions</p>
                          ) : (
                            <ul className="space-y-2">
                              {role.questions.map(q => (
                                <li key={q._id} className="flex items-start gap-2">
                                  <span className="text-ink-muted mt-0.5">-</span>
                                  <div className="flex-1">
                                    <span className="text-sm text-ink-secondary">{q.questionText}</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {q.tags.map(tag => (
                                        <span
                                          key={tag}
                                          className="kb-tone-neutral px-1.5 py-0.5 text-xs rounded-full"
                                        >
                                          {SALES_PLAYBOOK_TAG_LABELS[tag]}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* Deal Progression Section */}
                        <div className="bg-surface-subtle rounded-lg p-3 md:rounded-l-none md:pl-4">
                          <h4 className="text-sm font-semibold text-ink-secondary uppercase tracking-wide mb-2">
                            Deal Progression
                          </h4>
                          {(!role.advanced || role.advanced.length === 0) ? (
                            <p className="text-sm text-ink-muted italic">No deal progression items</p>
                          ) : (
                            <ul className="space-y-1">
                              {role.advanced
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map(item => (
                                  <li key={item._id} className="flex items-start gap-2">
                                    <span className="text-ink-muted mt-0.5">-</span>
                                    <span className="text-sm text-ink-secondary">{item.text}</span>
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      </>
      )}

      {/* Modal */}
      <AddSalesPlaybookModal
        isOpen={isAddSalesPlaybookModalOpen}
        onClose={() => {
          setIsAddSalesPlaybookModalOpen(false);
          setEditingPlaybook(null);
        }}
        onSave={handleSaveSalesPlaybook}
        existingPlaybookNames={playbooks.map(p => p.displayName)}
        editingPlaybook={editingPlaybook}
      />
      <PlaybookIngestionReview
        isOpen={isAiDraftReviewOpen}
        onClose={() => setIsAiDraftReviewOpen(false)}
        onSynced={fetchPlaybooks}
        setError={setError}
        setSuccess={setSuccess}
        initialNewDraftMode={draftReviewEntry === 'generate' ? 'generate' : undefined}
      />
    </div>
  );
};

export default SalesPlaybookManagement;
